import { provideAuth0 } from '@auth0/auth0-angular';

import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { API_BASE_URL, devLog, setWebComponentName, TOKEN_REQUEST_EVENT, TOKEN_SYNC_EVENT } from './jwt.constant';
import { MIJN_AUTH_SESSIE_MANAGER_API_BASE_URL, MIJN_AUTH_SESSIE_MANAGER_ENVIRONMENT } from './mijn-auth-sessie-manager.model';
import { CONTEXT_ID_CLAIM, CUSTOMER_ID_CLAIM, MijnJwtService } from './mijn-jwt.service';
import { FetchInterceptorService } from './fetch.interceptor.service';
import { JwtInterceptor } from './jwt.interceptor';
import { AuthTokenSyncService } from './auth-token-sync.service';
import { SESSION_STORAGE_CACHE, SessionStorageCache } from './session-storage-cache';

export interface MijnAuthSessieManagerOptions {
    apiBaseUrl?: string | null;
}

export function provideMijnJwtInterceptor(options: MijnAuthSessieManagerOptions = {}): EnvironmentProviders {
    return makeEnvironmentProviders([
        { provide: API_BASE_URL, useValue: options.apiBaseUrl ?? MIJN_AUTH_SESSIE_MANAGER_API_BASE_URL },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        FetchInterceptorService,
        provideAppInitializer(() => {
            const fetchInterceptor = inject(FetchInterceptorService);
            const jwtService = inject(MijnJwtService);

            const webComponentName = inject<string>('webComponentName' as any, { optional: true });
            setWebComponentName(webComponentName ?? null);

            const token$ = new BehaviorSubject<string | null>(null);

            window.addEventListener(TOKEN_SYNC_EVENT, (event: Event) => {
                const token = (event as CustomEvent<string | null>).detail;
                devLog('[JwtInterceptor] TOKEN_SYNC_EVENT received, token:', token ? 'present' : 'null');
                token$.next(token);
                jwtService.setToken(token);
            });

            jwtService.setObservables(
                token$.pipe(map((token) => token !== null)),
                token$.pipe(map((token) => getClaimFromToken(token, CUSTOMER_ID_CLAIM))),
                token$.pipe(map((token) => getClaimFromToken(token, CONTEXT_ID_CLAIM)))
            );

            fetchInterceptor.install();

            devLog('[JwtInterceptor] dispatching TOKEN_REQUEST_EVENT');
            window.dispatchEvent(new CustomEvent(TOKEN_REQUEST_EVENT));
        })
    ]);
}

function getClaimFromToken(token: string | null, claim: string): string | null {
    if (!token) {
        return null;
    }
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload[claim] ?? null;
    } catch {
        return null;
    }
}

export function provideMijnAuthSessieManager(options: MijnAuthSessieManagerOptions = {}): EnvironmentProviders {
    const cache = new SessionStorageCache();
    return makeEnvironmentProviders([
        provideMijnJwtInterceptor(options),
        { provide: SESSION_STORAGE_CACHE, useValue: cache },
        AuthTokenSyncService,
        provideAppInitializer(() => inject(AuthTokenSyncService).install()),
        provideAuth0({
            domain: MIJN_AUTH_SESSIE_MANAGER_ENVIRONMENT.domain,
            clientId: MIJN_AUTH_SESSIE_MANAGER_ENVIRONMENT.clientId,
            authorizationParams: {
                audience: MIJN_AUTH_SESSIE_MANAGER_ENVIRONMENT.audience,
                scope: 'openid profile email product_agreements offline_access'
            },
            cache,
            useRefreshTokens: true,
            useCookiesForTransactions: true
        })
    ]);
}
